import React, { useEffect, useRef, useState } from 'react';
import { useTour } from '@/App';
import { TOURS } from '@/tours/definitions';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';

const CARD_WIDTH = 320;
const CARD_MARGIN = 12;

// Wartet auf das Zielelement eines Tour-Schritts — direkt nach einem Seitenwechsel (pendingTourId)
// oder nach einem `before()`-Klick (z.B. ein Dropdown öffnen) ist es oft noch nicht im DOM.
// Gibt nach ~3s auf und liefert null zurück statt endlos zu pollen.
function useStepTarget(selector, retryKey) {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    if (!selector) { setTarget(null); return undefined; }
    let cancelled = false;
    let attempts = 0;
    let timer;
    const tryFind = () => {
      if (cancelled) return;
      const element = document.querySelector(selector);
      if (element) {
        // Ziel kann außerhalb des sichtbaren Bereichs liegen — nicht nur beim Fenster, auch
        // innerhalb eines scrollbaren Containers wie dem Scouter-Dialog (overflow-y-auto).
        // scrollIntoView läuft alle scrollbaren Vorfahren hoch, "nearest" scrollt nur so viel
        // wie nötig, statt das Ziel jedes Mal stur zu zentrieren.
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        setTarget(element);
        return;
      }
      if (attempts++ < 30) timer = setTimeout(tryFind, 100);
      else setTarget(null);
    };
    setTarget(null);
    tryFind();
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, retryKey]);
  return target;
}

// Rect des Zielelements aktuell halten — bei Resize/Scroll neu berechnen, damit Spotlight und
// Tooltip nicht "einfrieren", wenn während der Tour gescrollt oder das Fenster verändert wird.
function useLiveRect(element) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!element) { setRect(null); return undefined; }
    const update = () => setRect(element.getBoundingClientRect());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // Kurzes Polling statt eines MutationObserver auf dem ganzen <body> — reicht, um Layout-
    // Verschiebungen durch z.B. nachladende Badges/Zahlen einzufangen, ohne bei jeder DOM-
    // Änderung irgendwo in der App (Tippen, Live-Updates) mitzufeuern.
    const interval = setInterval(update, 400);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      clearInterval(interval);
    };
  }, [element]);
  return rect;
}

// spot ist {top, left, width, height} in Viewport-Pixeln (siehe useLiveRect + Padding weiter
// unten) — bewusst kein DOMRect, damit auch der berechnete Spotlight-Ausschnitt hier reinpasst.
function cardPosition(spot) {
  if (!spot) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const spotBottom = spot.top + spot.height;
  const spaceBelow = viewportHeight - spotBottom;
  const placeBelow = spaceBelow > 220 || spaceBelow > spot.top;
  const top = placeBelow ? spotBottom + CARD_MARGIN : undefined;
  const bottom = placeBelow ? undefined : viewportHeight - spot.top + CARD_MARGIN;
  let left = spot.left;
  if (left + CARD_WIDTH > viewportWidth - CARD_MARGIN) left = viewportWidth - CARD_WIDTH - CARD_MARGIN;
  if (left < CARD_MARGIN) left = CARD_MARGIN;
  return { top, bottom, left, transform: 'none' };
}

export default function TourOverlay() {
  const { activeTourId, stepIndex, pendingTourId, currentRoute, startTour, next, back, stop, clearPending } = useTour();
  const tour = TOURS.find((candidate) => candidate.id === activeTourId);
  const step = tour?.steps[stepIndex];
  const beforeRanForRef = useRef(-1);

  // Tour, die von einer anderen Seite aus angestoßen wurde: sobald deren Route erreicht ist,
  // automatisch starten (siehe TourProvider.goToTour in App.js).
  useEffect(() => {
    if (!pendingTourId) return;
    const pendingTour = TOURS.find((candidate) => candidate.id === pendingTourId);
    if (pendingTour && pendingTour.route === currentRoute) {
      startTour(pendingTourId);
    } else if (!pendingTour) {
      clearPending();
    }
  }, [pendingTourId, currentRoute, startTour, clearPending]);

  // Verlässt der Nutzer die Route der laufenden Tour (z.B. über die Navigation), beenden wir sie —
  // ihre Zielelemente existieren auf der neuen Seite ohnehin nicht mehr.
  useEffect(() => {
    if (tour?.route && tour.route !== currentRoute) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute]);

  // Manche Schritte lösen über `before` eine echte Aktion aus (z.B. eine Beispielzeile
  // markieren, um die Mehrfachauswahl-Leiste zu zeigen). `tour.cleanup` macht das rückgängig,
  // sobald die Tour endet — egal ob per "Fertig", X, Esc, Klick daneben oder Routenwechsel.
  useEffect(() => {
    const activeTour = tour;
    return () => { if (activeTour) activeTour.cleanup?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourId]);

  useEffect(() => {
    if (!step || beforeRanForRef.current === stepIndex) return;
    beforeRanForRef.current = stepIndex;
    step.before?.();
  }, [step, stepIndex]);

  useEffect(() => {
    if (!activeTourId) beforeRanForRef.current = -1;
  }, [activeTourId]);

  const target = useStepTarget(step?.target, `${activeTourId}-${stepIndex}`);
  const rect = useLiveRect(target);

  useEffect(() => {
    if (!step) return;
    const onKeyDown = (event) => { if (event.key === 'Escape') stop(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, stop]);

  if (!tour || !step) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === tour.steps.length - 1;
  const pad = 6;
  const spot = rect ? {
    top: rect.top - pad, left: rect.left - pad,
    width: rect.width + pad * 2, height: rect.height + pad * 2,
  } : null;
  const position = cardPosition(spot);

  return (
    // pointer-events-auto ist Pflicht: Ein offener Radix-Dialog (z.B. der Scouter) setzt
    // pointer-events:none auf <body>, um Klicks außerhalb seines eigenen Inhalts zu sperren —
    // ohne die explizite Rücknahme hier wären "Weiter"/"Zurück"/X in diesem Overlay unsichtbar
    // tot, weil sie das gesperrte pointer-events:none einfach erben würden.
    <div className="pointer-events-auto fixed inset-0 z-[200]" data-tour-overlay="true" role="dialog" aria-modal="true" aria-label={`Tour: ${tour.label}`}>
      {spot ? (
        <>
          <div className="fixed bg-black/60 transition-all duration-200" style={{ top: 0, left: 0, right: 0, height: Math.max(0, spot.top) }} onClick={stop} />
          <div className="fixed bg-black/60 transition-all duration-200" style={{ top: spot.top + spot.height, left: 0, right: 0, bottom: 0 }} onClick={stop} />
          <div className="fixed bg-black/60 transition-all duration-200" style={{ top: spot.top, left: 0, width: Math.max(0, spot.left), height: spot.height }} onClick={stop} />
          <div className="fixed bg-black/60 transition-all duration-200" style={{ top: spot.top, left: spot.left + spot.width, right: 0, height: spot.height }} onClick={stop} />
          <div
            className="fixed rounded-lg ring-2 ring-primary transition-all duration-200"
            style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
            onClick={stop}
          />
        </>
      ) : (
        <div className="fixed inset-0 bg-black/60" onClick={stop} />
      )}

      <div
        className="fixed z-[210] w-80 max-w-[calc(100vw-24px)] space-y-3 rounded-xl border bg-popover p-4 text-popover-foreground shadow-xl"
        style={position}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" /> {tour.label} · Schritt {stepIndex + 1}/{tour.steps.length}
          </div>
          <button type="button" onClick={stop} aria-label="Tour beenden" className="rounded-sm text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h4 className="font-semibold leading-snug">{step.title}</h4>
        <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" disabled={isFirst} onClick={back}>
            <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Zurück
          </Button>
          {isLast ? (
            <Button type="button" size="sm" onClick={stop}>Fertig</Button>
          ) : (
            <Button type="button" size="sm" onClick={next}>Weiter <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}
